import { Availability } from '../schemas';
import * as xmlUtils from './utils/xml';
import * as seatUtils from './utils/seatUtils';
import decrypt from './utils/decrypt';

/**
 * Initialize the availability parser with an array of valid priceCategories and blocking-codes
 * @param {Array<String>} priceCategories a list of priceCategories: ['0', '1', '2'];
 * @param {Array<Number>} blockingCodes a list of blocking codes: [0, 1, 2];
 * @returns {Function}
 */
export default function (priceCategories, blockingCodes) {

  /**
   * This function takes as the input the ray availability from the Availability Service.
   * The availability is Encrypted, lzipped and annoying to work with.
   * It looks something like this:
   * ```
   * <xml><ev_comp>CwEB{...}wga</ev_comp></xml>
   * ```
   *
   * After some "magic" parsing it returns an object that looks like this:
   *
   * ```javascript
   * return {
   *  'S-L-12-4': { // seat ID (section-block-row-seat)
   *     available:  true, // or false
   *     priceLevel: 1, // price category of the seat
   *     attributes: [`pc-${priceCategory}`] // seat attributes
   *   },
   *   {...} // all other seat availability
   * }
   * ```
   *
   * If you want to know more about the schema-availability you can look at this
   *
   * [Availability Information](../../sim/wikis/availability)
   *
   * @param rawAvailability
   * @returns {Object|Error}
   */
  return function (rawAvailability) {
    rawAvailability = rawAvailability.replace('<?xml version="1.0" encoding="UTF-8" ?>', '');
    var mainAvailability = {};

    var parsedXml = xmlUtils.parseXML(rawAvailability);
    if (parsedXml instanceof Error || xmlUtils.isParsedError(parsedXml)) {
      return new Error('MNX Availability parser - Error parsing the raw xml file. Might be an invalid XML.');
    }

    var envComp = parsedXml.firstChild;
    if (!envComp) {
      return new Error('MNX Availability parser - XML has wrong format, can not find <ev_comp/>');
    }

    var encryptedAvailability = envComp.firstChild;
    if (!encryptedAvailability) {
      return new Error('MNX Availability parser - XML has wrong format, can not read the content of ev_comp');
    }

    try {
      // trim fixed iOS bug
      var decryptedAvailabilityXML = decrypt(encryptedAvailability.textContent.trim());
      var decryptedAvailability = xmlUtils.parseXML(decryptedAvailabilityXML);
    }
    catch (e) {
      return new Error('MNX Availability parser - XML has wrong format, error in decrypting XML');
    }

    try {
      var mainElement = decryptedAvailability.getElementsByTagName('Event_details')[0];
      var eventDetails = xmlUtils.getChildElement(mainElement, 'E');
      var sectionList = xmlUtils.getChildElementsAndE(eventDetails, 'Section_id_list');

      // For every section of the xml, decode its seat availability
      sectionList.forEach(sectionEl => {
        var priceCategory = xmlUtils.getChildElement(sectionEl, 'Price_level').textContent;

        var key = xmlUtils.getChildElement(sectionEl, 'Section_name').textContent;
        key += '-' + xmlUtils.getChildElementsAndE(sectionEl, 'Row_names')[0].textContent || '0';
        // TODO, what if there are more rows?

        var seatNames = xmlUtils.getChildElementsAndE(sectionEl, 'Seat_names');
        var extendedSeatDataArray = seatUtils.extendedSeatDataToArray(xmlUtils.getChildElementsAndE(sectionEl, 'Esd'));

        seatNames.forEach((seatName, idx) => {
          var seatKey = key + '-' + seatName.textContent || '0';
          // There should be a one to one mapping between the seat names and the qualifier bits
          var qualifierBits = extendedSeatDataArray[idx];
          var seatBlockingCode = seatUtils.extractBlockingCode(qualifierBits);

          mainAvailability[seatKey] = new Availability({
            available:  seatUtils.seatIsAvailable(
              priceCategories, priceCategory, qualifierBits, blockingCodes, seatBlockingCode
            ),
            priceLevel: priceCategory,
            attributes: [`pc-${priceCategory}`],
            blockingCode: (seatBlockingCode > 0) ? seatBlockingCode : 0
          });
        });
      });
    }
    catch (e) {
      console.error(e);
      return new Error('MNX Availability parser - XML has wrong format');
    }

    return mainAvailability;
  };
};



// WEBPACK FOOTER //
// ./frontend/react/sim/parser-mnx-availability/index.js